import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { TenantService } from '../services/tenant.service';

/**
 * Guard de autorização das rotas administrativas do TENANT
 * (`/:tenantId/configuracoes/**`).
 *
 * Complementa `authGuard` + `tenantGuard`: além de autenticado e com tenant
 * válido, o usuário precisa ser **administrador do tenant** (`isTenantAdmin`).
 *
 * Fail-closed: sem papel administrativo → acesso negado. O papel vem do JWT
 * (`Acesso-Admin`) ou do tenant ativo (`SelectedTenant.role`, reidratado da api antes de
 * negar); em acesso negado, redireciona para
 * o dashboard do tenant (comportamento seguro, sem quebrar deep links válidos).
 *
 * Não substitui a autorização do backend — é a camada de rota do frontend.
 */
export const tenantAdminGuard: CanActivateFn = (route) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const tenantService = inject(TenantService);

  const tenantId = route.pathFromRoot
    .map(r => r.paramMap.get('tenantId'))
    .find((id): id is string => !!id) ?? null;

  const deny = () =>
    tenantId
      ? router.createUrlTree(['/', tenantId, 'dashboard'])
      : router.createUrlTree(['/auth/login']);

  if (auth.isTenantAdmin()) return true;

  // Antes de negar, reidrata o papel no tenant: `hub_tenant` antigo guarda o cargo do perfil
  // e o admin pode ter ganhado/perdido a flag desde a seleção da empresa (#969).
  const user$: Observable<unknown> = auth.user() ? of(null) : auth.loadCurrentUser();
  return user$.pipe(
    switchMap(() => tenantService.refreshRole()),
    map((): boolean | UrlTree => (auth.isTenantAdmin() ? true : deny())),
  );
};
